import {NarrowPage} from "../../elements/Pages";
import {React, useEffect, useState} from "react";
import Joi from "joi"
import {useQuery, useQueryClient} from '@tanstack/react-query'
import {Input, Checkbox, Select} from "../../elements/forms/Inputs"
import {FormSectionContent} from "../../elements/forms/FormLayout"
import Form, {baseHandleChange} from "../../elements/forms/Form"
import {useNavigate, useParams} from "react-router-dom";
import {toast} from "react-toastify";
import {getClient, patchClient, postClient, deleteClient} from "../../../api/clients";
import {getTenantsWithClients} from "../../../api/accounts";
import {getSlackChannels, patchSlackChannel} from "../../../api/slack";
import {useAuth} from "../../../contexts/AuthContext";
import {AddButton, SecondaryButton} from "../../elements/Buttons";
import {DeleteImmediatelyButton} from "../../elements/DeleteButton";
import {ArrowTopRightOnSquareIcon, HashtagIcon, LockClosedIcon} from "@heroicons/react/20/solid";
import {Link} from "react-router-dom";


export default function ClientCreateEdit() {

    const {currentUser, permissions} = useAuth()
    const queryClient = useQueryClient()
    const {id} = useParams()
    const [data, setData] = useState({
        id: null,
        tenant: null,
        name: "",
        code: "",
        email_addresses: "",
        google_drive_root_folder_id: "",
        asana_projects: []
    })

    const {data: tenants} = useQuery(['tenants_with_clients', currentUser.id], getTenantsWithClients, {refetchInterval: 1000 * 60 * 60 * 24})

    // Fetch Slack channels mapped to this client
    const {data: slackChannels, refetch: refetchSlackChannels} = useQuery(
        ['slack_channels_for_client', id],
        () => getSlackChannels({client: id}),
        {enabled: !!id, refetchInterval: 1000 * 60 * 2}
    )

    const [errors, setErrors] = useState({}) // validation errors
    const [asanaProjectsErrors, setAsanaProjectsErrors] = useState([])
    const [newRowId, setNewRowId] = useState(1)

    let navigate = useNavigate();

    useEffect(() => {
        loadData()
    }, [id])

    useEffect(() => {
        // Preselect tenant if there's only one and we're creating a new client
        if (!id && !data.tenant && tenants && tenants.length === 1) {
            setData(prevData => ({
                ...prevData,
                tenant: tenants[0].id
            }))
        }
    }, [tenants, id, data.tenant])

    async function loadData() {
        if (!id) return
        const newData = await getClient(id)
        setData(newData)
    }

    const schema = {
        tenant: Joi.number().required().messages({"number.base": "Please select a team"}),
        name: Joi.string().required().label("Name"),
        code: Joi.string().required().label("Code"),
        email_addresses: Joi.string().allow('', null).label("Email Addresses"),
        google_drive_root_folder_id: Joi.string().allow('', null).label("Google Drive Root Folder ID")
    };

    const title = (id || data.id) ? "Edit Client" : "Add New Client"
    const successMessage = (id || data.id) ? "Client updated" : "Client created"

    async function onSave() {
        let newData
        if (!data.id) newData = await postClient(data)
        else newData = await patchClient(data)

        setData(newData)
        toast.success(successMessage)
        queryClient.invalidateQueries({ queryKey: ["clients", currentUser.id] })
        navigate(`/clients`)
    }

    async function onDelete() {
        await deleteClient(data.id)
        queryClient.invalidateQueries({ queryKey: ["clients", currentUser.id] })
        toast.success("Client deleted.")
        navigate(`/clients`)
    }

    function handleChange(evt) {
        baseHandleChange(evt, data, setData, errors, setErrors, schema)
    }

    function handleListChange(evt, rowId) {
        const newProjects = data.asana_projects.map(row => {
            if (row.id === rowId) {
                let value = evt.target.value
                if (evt.currentTarget && evt.currentTarget.type === 'checkbox') {
                    value = evt.currentTarget.checked
                }
                // Set empty string to null
                if (value === "") {
                    value = null
                }
                return {...row, [evt.target.name]: value}
            }
            return row
        })

        setData({...data, asana_projects: newProjects})
    }

    function addAsanaProject() {
        const newProject = {
            id: `N${newRowId}`,
            asana_project_id: "",
            last_polled: null,
            is_active: true
        }
        setNewRowId(newRowId + 1)
        setData({
            ...data,
            asana_projects: [...data.asana_projects, newProject]
        })
    }

    function deleteAsanaProject(projectId) {
        const newProjects = data.asana_projects.filter(project => project.id !== projectId)
        setData({
            ...data,
            asana_projects: newProjects
        })
    }

    async function removeSlackChannel(channelId) {
        try {
            await patchSlackChannel(channelId, {client: null})
            toast.success("Channel removed from client")
            refetchSlackChannels()
            queryClient.invalidateQueries(['slack_channels'])
        } catch (e) {
            toast.error("Failed to remove channel")
        }
    }

    return (

        <NarrowPage title={title}>

            <Form id="createEditClient" onSave={onSave}
                  onDelete={onDelete}
                  data={data} setData={setData}
                  errors={errors} setErrors={setErrors}
                  successMessage={successMessage}
                  schema={schema}>

                <FormSectionContent>

                    {tenants && tenants.length > 1 && (
                        <Select
                            path="tenant"
                            label="Team"
                            className="sm:col-span-6 text-left"
                            options={tenants}
                            valueAttr="id"
                            labelAttr="name"
                            onChange={handleChange}
                            data={data}
                            errors={errors}
                            isClearable={false}
                            valueOnly={true}
                        />
                    )}

                    <Input
                        path="name"
                        type="text"
                        label="Name"
                        className="sm:col-span-6"
                        onChange={handleChange}
                        errors={errors}
                        data={data}
                    />

                    <Input
                        path="code"
                        type="text"
                        label="Code"
                        className="sm:col-span-6"
                        onChange={handleChange}
                        errors={errors}
                        data={data}
                    />

                    <Input
                        path="email_addresses"
                        type="text"
                        label="Email Addresses"
                        className="sm:col-span-6"
                        onChange={handleChange}
                        errors={errors}
                        data={data}
                    />

                    <div className="sm:col-span-6">
                        <div className="flex items-center gap-2">
                            <div className="flex-1">
                                <Input
                                    path="google_drive_root_folder_id"
                                    type="text"
                                    label="Google Drive Root Folder ID"
                                    onChange={handleChange}
                                    errors={errors}
                                    data={data}
                                />
                            </div>
                            {data.google_drive_root_folder_id && (
                                <a
                                    href={`https://drive.google.com/drive/folders/${data.google_drive_root_folder_id}`}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="mt-6 text-indigo-600 hover:text-indigo-900"
                                    title="Open in Google Drive"
                                >
                                    <ArrowTopRightOnSquareIcon className="h-5 w-5" aria-hidden="true"/>
                                </a>
                            )}
                        </div>
                    </div>



                    {/* Asana Projects Section */}
                    <div className="mt-8 sm:col-span-6">
                        <div className="flex justify-between items-center mb-4">
                            <h3 className="text-lg font-medium text-gray-900">Asana Projects</h3>
                            <AddButton onClick={addAsanaProject} label="Add Asana Project"/>
                        </div>

                        {data.asana_projects && data.asana_projects.length > 0 && (
                            <div className="overflow-x-auto">
                                <table className="min-w-full divide-y divide-gray-300">
                                    <thead className="bg-gray-50">
                                    <tr>
                                        <th scope="col" className="py-3.5 pl-4 pr-3 text-left text-sm font-semibold text-gray-900">
                                            Asana Project ID
                                        </th>
                                        <th scope="col" className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">
                                            Last Polled
                                        </th>
                                        <th scope="col" className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">
                                            Active
                                        </th>
                                        <th scope="col" className="relative py-3.5 pl-3 pr-4">
                                            <span className="sr-only">Actions</span>
                                        </th>
                                    </tr>
                                    </thead>
                                    <tbody className="divide-y divide-gray-200 bg-white">
                                    {data.asana_projects.map((project, index) => (
                                        <tr key={project.id} className={index % 2 === 0 ? undefined : 'bg-gray-50'}>
                                            <td className="whitespace-nowrap py-4 pl-4 pr-3 text-sm">
                                                <div className="flex items-center gap-2">
                                                    <div className="flex-1">
                                                        <Input
                                                            path="asana_project_id"
                                                            type="text"
                                                            className="sm:col-span-6"
                                                            onChange={handleListChange}
                                                            errors={asanaProjectsErrors}
                                                            data={data.asana_projects}
                                                            rowId={project.id}
                                                        />
                                                    </div>
                                                    {project.asana_project_id && (
                                                        <a
                                                            href={`https://app.asana.com/0/${project.asana_project_id}`}
                                                            target="_blank"
                                                            rel="noopener noreferrer"
                                                            className="text-indigo-600 hover:text-indigo-900"
                                                            title="Open in Asana"
                                                        >
                                                            <ArrowTopRightOnSquareIcon className="h-5 w-5" aria-hidden="true"/>
                                                        </a>
                                                    )}
                                                </div>
                                            </td>
                                            <td className="whitespace-nowrap px-3 py-4 text-sm">
                                                <Input
                                                    path="last_polled"
                                                    type="datetime-local"
                                                    className="sm:col-span-6"
                                                    onChange={handleListChange}
                                                    errors={asanaProjectsErrors}
                                                    data={data.asana_projects}
                                                    rowId={project.id}
                                                />
                                            </td>
                                            <td className="whitespace-nowrap px-3 py-4 text-sm">
                                                <Checkbox
                                                    path="is_active"
                                                    label="Active"
                                                    className="sm:col-span-6"
                                                    onChange={handleListChange}
                                                    errors={asanaProjectsErrors}
                                                    data={data.asana_projects}
                                                    rowId={project.id}
                                                    hideLegend={true}
                                                />
                                            </td>
                                            <td className="relative whitespace-nowrap py-4 pl-3 pr-4 text-right text-sm">
                                                <DeleteImmediatelyButton
                                                    onDelete={() => deleteAsanaProject(project.id)}
                                                    iconOnly={true}
                                                />
                                            </td>
                                        </tr>
                                    ))}
                                    </tbody>
                                </table>
                            </div>
                        )}
                    </div>

                    {/* Slack Channels Section - only show for existing clients */}
                    {id && (
                        <div className="mt-8 sm:col-span-6">
                            <div className="flex justify-between items-center mb-4">
                                <h3 className="text-lg font-medium text-gray-900">Slack Channels</h3>
                                <Link to="/settings/integrations/slack">
                                    <SecondaryButton label="Manage Channels"/>
                                </Link>
                            </div>

                            {slackChannels && slackChannels.length > 0 ? (
                                <div className="overflow-x-auto">
                                    <table className="min-w-full divide-y divide-gray-300">
                                        <thead className="bg-gray-50">
                                        <tr>
                                            <th scope="col" className="py-3.5 pl-4 pr-3 text-left text-sm font-semibold text-gray-900">
                                                Channel
                                            </th>
                                            <th scope="col" className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">
                                                Workspace
                                            </th>
                                            <th scope="col" className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">
                                                Sync Status
                                            </th>
                                            <th scope="col" className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">
                                                Last Synced
                                            </th>
                                            <th scope="col" className="relative py-3.5 pl-3 pr-4">
                                                <span className="sr-only">Actions</span>
                                            </th>
                                        </tr>
                                        </thead>
                                        <tbody className="divide-y divide-gray-200 bg-white">
                                        {slackChannels.map((channel, index) => (
                                            <tr key={channel.id} className={index % 2 === 0 ? undefined : 'bg-gray-50'}>
                                                <td className="whitespace-nowrap py-4 pl-4 pr-3 text-sm">
                                                    <div className="flex items-center">
                                                        {channel.is_private ? (
                                                            <LockClosedIcon className="h-4 w-4 text-gray-400 mr-1"/>
                                                        ) : (
                                                            <HashtagIcon className="h-4 w-4 text-gray-400 mr-1"/>
                                                        )}
                                                        <span className="font-medium text-gray-900">
                                                            {channel.channel_name}
                                                        </span>
                                                    </div>
                                                </td>
                                                <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">
                                                    {channel.workspace_name}
                                                </td>
                                                <td className="whitespace-nowrap px-3 py-4 text-sm">
                                                    {channel.is_sync_enabled ? (
                                                        <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-green-100 text-green-800">
                                                            Enabled
                                                        </span>
                                                    ) : (
                                                        <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-gray-100 text-gray-800">
                                                            Disabled
                                                        </span>
                                                    )}
                                                </td>
                                                <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">
                                                    {channel.last_polled
                                                        ? new Date(channel.last_polled).toLocaleString()
                                                        : 'Never'}
                                                </td>
                                                <td className="relative whitespace-nowrap py-4 pl-3 pr-4 text-right text-sm">
                                                    <DeleteImmediatelyButton
                                                        onDelete={() => removeSlackChannel(channel.id)}
                                                        iconOnly={true}
                                                        label="Remove from client"
                                                    />
                                                </td>
                                            </tr>
                                        ))}
                                        </tbody>
                                    </table>
                                </div>
                            ) : (
                                <div className="text-center py-6 border border-gray-200 rounded-lg">
                                    <HashtagIcon className="mx-auto h-8 w-8 text-gray-400"/>
                                    <p className="mt-2 text-sm text-gray-500">
                                        No Slack channels mapped to this client.
                                    </p>
                                    <p className="text-sm text-gray-500">
                                        <Link to="/settings/integrations/slack" className="text-indigo-600 hover:text-indigo-900">
                                            Go to Slack settings
                                        </Link> to map channels.
                                    </p>
                                </div>
                            )}
                        </div>
                    )}

                </FormSectionContent>

            </Form>

            <span onClick={() => navigate("/clients")}
                  className="cursor-pointer float-right mt-2 font-medium text-imsblue-600">Back to list</span>

        </NarrowPage>
    )
}
